import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Keyboard } from '@mui/icons-material';
import { retroColors, vortxTypography } from '@vortx/shared-ui';

const RetroStatusBar = ({ currentApp, appConfig, userEmail, currentProject, currentScenario }) => {
    const accentColor = appConfig?.color || '#b28c50';
    const [currentTime, setCurrentTime] = useState(new Date());

    // Update clock every second
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Generate clearance ID from email (same logic as RetroSystemStatus)
    const clearanceId = userEmail
        ? `VX-${userEmail.split('@')[0].slice(0, 3).toUpperCase()}-${Math.abs(userEmail.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 9000 + 1000)}`
        : 'VX-OPR-0000';

    // Format time as HH:MM:SS
    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    // Format coordinates
    const formatCoord = (value, isLat) => {
        if (!value && value !== 0) return null;
        const abs = Math.abs(value).toFixed(4);
        const dir = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
        return `${abs}°${dir}`;
    };

    // Build status message
    const getStatusMessage = () => {
        const lat = currentProject?.latitude || currentProject?.lat;
        const lng = currentProject?.longitude || currentProject?.lng || currentProject?.lon;

        if (currentScenario?.name) {
            if (lat && lng) {
                return `analyzing ${currentScenario.name} @ ${formatCoord(lat, true)} ${formatCoord(lng, false)}`;
            }
            return `analyzing ${currentScenario.name}`;
        }

        if (currentProject?.name) {
            if (lat && lng) {
                return `viewing ${currentProject.name} @ ${formatCoord(lat, true)} ${formatCoord(lng, false)}`;
            }
            return `viewing ${currentProject.name}`;
        }

        return 'standing by';
    };

    const shortcuts = [
        { key: 'Ctrl+N', action: 'New Project' },
        { key: 'Ctrl+P', action: 'Projects' },
        { key: '?', action: 'Help' },
    ];

    return (
        <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            borderTop: `1px solid ${accentColor}40`,
            zIndex: 100,
            transition: 'border-color 0.3s ease',
        }}>
            {/* Use same grid as main layout to align center section */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(200px, 15%) 1fr minmax(220px, 18%)',
                    lg: 'minmax(220px, 16%) 1fr minmax(260px, 20%)',
                },
                alignItems: 'center',
                padding: '0.5rem 0',
            }}>
                {/* Left - Keyboard Shortcuts */}
                <Box sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 2,
                    pl: 2,
                }}>
                    <Keyboard sx={{ fontSize: 14, color: retroColors.dimmed }} />
                    {shortcuts.map(({ key, action }) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.6rem',
                                padding: '0.1rem 0.3rem',
                                background: 'rgba(63, 106, 100, 0.3)',
                                border: '1px solid rgba(63, 106, 100, 0.5)',
                                color: retroColors.accent,
                            }}>
                                {key}
                            </Typography>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.6rem',
                                color: retroColors.muted,
                            }}>
                                {action}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Center - Operator Status (centered in middle panel) */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    px: 2,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: accentColor,
                        fontWeight: 600,
                    }}>
                        {clearanceId}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.dimmed,
                    }}>
                        |
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.muted,
                        fontStyle: 'italic',
                    }}>
                        {getStatusMessage()}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.dimmed,
                    }}>
                        |
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: retroColors.accent,
                        fontVariantNumeric: 'tabular-nums',
                    }}>
                        {formatTime(currentTime)}
                    </Typography>
                </Box>

                {/* Right - Version */}
                <Box sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'flex-end',
                    pr: 2,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.dimmed,
                    }}>
                        VORTX FLUX v2.4
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default RetroStatusBar;
